import { AnyAction } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import { App, Immobilie, Produktlinie, VwgVorbelegungdaten } from '@ps20-frontend/frontend-models';
import { Produktlinienname, VwgVorvertragsdatenMerkmale } from '@ps20/bff-models';
import { IAppState } from '../appState';
import { isDenkmalschutzSelectableByValue } from '../../app/shared/selectors/gebaeude.selector';
import { GEBAEUDE_BESONDERHEITEN, HEIZTECHNIK_MERKMALE, GEBAEUDENUTZUNG_MERKMALE } from '../../app/shared/selectors/merkmale.selector';
import { VwgActions } from '../vwgActions';

export interface BuildingFeatures {
  basement: boolean;
  basementWithLivingSpace: boolean;
  woodenHouse: boolean;
  softRoofCovering: boolean;
  flatRoof: boolean;
  geothermal: boolean;
  solarThermal: boolean;
  photovoltaic: boolean;
  heritageProtection: boolean;
  vacationHome: boolean;
  rented: boolean;
  notPermanentlyOccupied: boolean;
  pool: boolean;
  poolIndoors: boolean;
  poolOutdoors: boolean;
  aussenpoolInHts: boolean | null;
  de1kzRoofCovering: boolean;
  buildingCategory: string;
  buildingClass: string;
  fireShellConstruction: boolean;
  noneApplies: boolean;
  gasHeating: boolean;
  oilHeating: boolean;
  districtHeating: boolean;
  pelletHeating: boolean;
  noHeatingApplies: boolean;
  noBuildingSpecialFeaturesApplies: boolean;
}

function resetBuildingSpecialFeatures(state: BuildingFeatures): void {
  GEBAEUDE_BESONDERHEITEN.forEach(feature => {
    state[feature] = false;
  });
  state.poolIndoors = false;
  state.poolOutdoors = false;
  state.basementWithLivingSpace = false;
}

function immobilieUebernehmen(
  sectionState: BuildingFeatures,
  payload: AnyAction['payload'],
  appState: IAppState,
): BuildingFeatures {
  if (payload.appId !== App.VWG_V2 || payload.angebotsId !== appState.angebotsId) {
    return sectionState;
  }

  const immobilie = payload.immobilie as Immobilie;
  const newState: BuildingFeatures = cloneDeep(sectionState);
  newState.basement = valueOrDefault(immobilie.kellerVorhanden, false);
  newState.basementWithLivingSpace = immobilie.kellerVorhanden
    ? immobilie.kellerMitWohnflaeche
    : null;
  newState.vacationHome = valueOrDefault(immobilie.ferienhaus, false);
  newState.flatRoof = valueOrDefault(immobilie.flachdach, false);
  return newState;
}

function merkmaleChanged(
  sectionState: BuildingFeatures,
  payload: AnyAction['payload'],
): BuildingFeatures {
  const newState: BuildingFeatures = { ...sectionState, ...payload };

  if (payload.basement === false) {
    newState.basementWithLivingSpace = false;
  }
  if (payload.pool === false) {
    newState.poolIndoors = false;
    newState.poolOutdoors = false;
    newState.aussenpoolInHts = null;
  }
  if (payload.noBuildingSpecialFeaturesApplies) {
    resetBuildingSpecialFeatures(newState);
  }

  if (payload.noHeatingApplies) {
    HEIZTECHNIK_MERKMALE.forEach(feature => {
      newState[feature] = false;
    });
  }

  if(payload.noneApplies){
    GEBAEUDENUTZUNG_MERKMALE.forEach(feature => {
      newState[feature] = false;
    });
  }
  if(payload.vacationHome){
    newState.notPermanentlyOccupied = false;
  }
  if(payload.notPermanentlyOccupied){
    newState.vacationHome = false;
  }

  return newState;
}

function produktlinienChanged(
  sectionState: BuildingFeatures,
  payload: AnyAction['payload'],
): BuildingFeatures {
  const produktlinien: Produktlinie[] = payload;
  const newState: BuildingFeatures = cloneDeep(sectionState);
  if (newState.poolOutdoors && newState.aussenpoolInHts == null) {
    const komfortLinie = produktlinien?.find(pl => pl.produktName === Produktlinienname.KOMFORT);
    const htsSelected = !komfortLinie?.leistungen?.find(l => l.id === 'hausTechnikSchutz')?.abgewaehlt;
    if (htsSelected) {
      newState.aussenpoolInHts = true;
    }
  }
  return newState;
}

export const merkmaleReducer = function(
  sectionState: BuildingFeatures = null,
  action: AnyAction,
  appState: IAppState,
): BuildingFeatures {
  const { payload, type } = action;

  switch (type) {
    case GlobalActions.IMMOBILIE_UEBERNEHMEN:
      return immobilieUebernehmen(sectionState, payload, appState);
    case VwgActions.AUSSENPOOL_IN_HTS_CHANGED: {
      return { ...sectionState, aussenpoolInHts: payload as boolean };
    }
    case VwgActions.MERKMALE_CHANGED:
      return merkmaleChanged(sectionState, payload);
    case VwgActions.CONSTRUCTION_YEAR_CHANGED: {
      const newState: BuildingFeatures = cloneDeep(sectionState);
      if (!isDenkmalschutzSelectableByValue(payload.constructionYear)) {
        newState.heritageProtection = false;
      }
      return newState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: VwgVorbelegungdaten = payload;
      const merkmale: VwgVorvertragsdatenMerkmale = actionPayload.merkmale;
      const newState: BuildingFeatures = cloneDeep(sectionState);
      if (actionPayload.forApp === App.VWG_V2 && actionPayload.angebotsId === appState.angebotsId) {
        applyMerkmale(newState, merkmale);
      }
      return newState;
    }
    case VwgActions.PRODUKT_VORBELEGT: {
      const vorbelegungdaten: VwgVorbelegungdaten = payload;
      const merkmale: VwgVorvertragsdatenMerkmale = vorbelegungdaten?.merkmale;
      if (!merkmale) {
        return sectionState;
      }
      const newState: BuildingFeatures = cloneDeep(sectionState);
      applyMerkmale(newState, merkmale);
      return newState;
    }
    case VwgActions.PRODUKTLINIEN_CHANGED:
      return produktlinienChanged(sectionState, payload);
    default:
      return sectionState;
  }
};

const MERKMALE_MAPPING: [keyof BuildingFeatures, keyof VwgVorvertragsdatenMerkmale, boolean][] = [
  ['basement', 'kellerVorhanden', false],
  ['basementWithLivingSpace', 'kellerMitWohnflaeche', false],
  ['woodenHouse', 'holzhaus', false],
  ['softRoofCovering', 'dachbelagWeich', false],
  ['flatRoof', 'flachdach', false],
  ['geothermal', 'geothermie', false],
  ['solarThermal', 'solarthermie', false],
  ['photovoltaic', 'photovoltaik', false],
  ['heritageProtection', 'denkmalschutz', false],
  ['vacationHome', 'ferienhaus', false],
  ['notPermanentlyOccupied', 'leerstehend', false],
  ['pool', 'schwimmbad', false],
  ['rented', 'vermietet', true],
  ['poolIndoors', 'schwimmbadInnen', true],
  ['poolOutdoors', 'schwimmbadAussen', true],
  ['gasHeating', 'gasheizung', true],
  ['oilHeating', 'oelheizung', true],
  ['pelletHeating', 'pelletheizung', true],
  ['districtHeating', 'fernNahwaerme', true],
];

function applyMerkmale(state: BuildingFeatures, merkmale: VwgVorvertragsdatenMerkmale): void {
  const target = state as unknown as Record<string, boolean | string | number>;
  for (const [stateKey, merkmaleKey, skipIfNull] of MERKMALE_MAPPING) {
    const value = merkmale[merkmaleKey];
    if (!skipIfNull || value != null) {
      target[stateKey] = value;
    }
  }
}

function valueOrDefault(value: boolean | undefined, defaultValue: boolean): boolean {
  return value || defaultValue;
}
